import React from 'react';
import { useParams } from 'react-router';
import styled from 'styled-components';
import { ProjectId, SpaceTabId } from 'api/models';
import { ProjectDetailTabKeys } from 'api/project';
import { PageLoading } from 'components/atoms/loading';
import { FilterBar } from 'components/molecules/filter-bar';
import { BreadCrumbs } from 'components/molecules/project-details/header/breadcrumbs';
import { ProjectTitle } from 'components/molecules/project-details/header/project-title';
import { ProjectDetailTabs } from 'components/molecules/project-details/header/tabs/tabs';
import { SidebarCollapseButton } from 'components/molecules/sidebar-collapse-button/SidebarCollapseButton';
import { CurrentSpaceContext } from 'components/organisms/spaces/context';
import {
  SpaceNewTabPaneContent,
  SpaceTabContainer,
} from 'components/organisms/spaces/space-tab';
import { SpaceSidebar } from 'components/organisms/spaces/space-tab/space-sidebar';
import { ProjectDetailsPageTemplate } from 'components/templates';
import { TOGGLE_PROJECTS_SIDEBAR } from 'constant/constant';
import { Template } from 'data/templates/api';
import { useDistractionFreeMode } from 'hooks/use-distraction-free-mode';
import { logger } from 'services';
import { EmptySpaces } from './project-spaces-empty';
import { Content, Layout, NewTabContainer } from './project-spaces.styles';
import { ProjectSpacesPageTypes, UrlParamTypes } from './project-spaces.types';
import { CreationType, useSpaceActions } from './use-space-actions';

export const FilterBarStyled = styled(FilterBar)`
  width: 100%;
  padding: var(--spacing-03) var(--spacing-03);
`;

export const ProjectSpacesPage = ({ project }: ProjectSpacesPageTypes) => {
  const { spaceTabId, spaceId } = useParams<UrlParamTypes>();
  const {
    currentSpaceDetails,
    projectSpacesList,
    projectSpacesFetchStatus,
    creatingDocumentType,
    canCreate,
    onSpaceDocCreate,
    onSpaceAdd,
  } = useSpaceActions({
    spaceId,
    project,
    spaceTabId,
  });

  const handleNewDocument = () => {
    return onSpaceDocCreate({
      type: CreationType.Doc,
      payload: {
        spaceId: Number(spaceId),
      },
    });
  };

  const handleNewEmbed = (values, form) => {
    const { url } = values;

    return onSpaceDocCreate({
      type: CreationType.Embed,
      payload: {
        spaceId: Number(spaceId),
        url,
        form,
      },
    });
  };

  const handleOnChooseTemplate = ({ templateId }: Template) => {
    return onSpaceDocCreate({
      type: CreationType.Template,
      payload: {
        spaceId: Number(spaceId),
        templateId,
      },
    });
  };

  const { active, exit, enter } = useDistractionFreeMode(
    TOGGLE_PROJECTS_SIDEBAR,
  );

  function sidebarToggle() {
    if (active) {
      exit();
    } else {
      enter();
    }
  }

  switch (projectSpacesFetchStatus) {
    case 'loading': {
      return <PageLoading />;
    }

    case 'success': {
      if (!canCreate) {
        logger.log("User can't create a space here.");
      }

      return (
        <ProjectDetailsPageTemplate
          title={<ProjectTitle project={project} />}
          tabs={
            <ProjectDetailTabs
              projectId={ProjectId(project.projectId)}
              activeTabKey={ProjectDetailTabKeys.Spaces}
            />
          }
          documentTitle={project.projectName}
          breadCrumbs={<BreadCrumbs />}
        >
          <Layout>
            <SpaceSidebar
              onSpaceAdd={onSpaceAdd}
              activeSpaceId={spaceId}
              activeSpaceTabId={spaceTabId ?? 'new'}
              project={project}
              projectSpaces={projectSpacesList}
            />

            <Content>
              {spaceId === 'new' && projectSpacesList.length <= 1 ? (
                <EmptySpaces onSpaceAdd={onSpaceAdd} />
              ) : currentSpaceDetails ? (
                <CurrentSpaceContext.Provider value={currentSpaceDetails}>
                  {spaceTabId !== 'new' ? (
                    <SpaceTabContainer
                      spaceTabId={SpaceTabId(
                        project.projectId,
                        Number(spaceId),
                        Number(spaceTabId),
                      )}
                    />
                  ) : (
                    <NewTabContainer>
                      <FilterBarStyled>
                        <FilterBar.Primary>
                          <SidebarCollapseButton
                            sidebarToggle={sidebarToggle}
                          />
                        </FilterBar.Primary>
                      </FilterBarStyled>
                      <SpaceNewTabPaneContent
                        isCreateInProgress={
                          creatingDocumentType === CreationType.Doc
                        }
                        isCreateFromTemplateInProgress={
                          creatingDocumentType === CreationType.Embed
                        }
                        isEmbedInProgress={
                          creatingDocumentType === CreationType.Template
                        }
                        onCreateDocument={handleNewDocument}
                        onCreateEmbed={handleNewEmbed}
                        onChooseTemplate={handleOnChooseTemplate}
                      />
                    </NewTabContainer>
                  )}
                </CurrentSpaceContext.Provider>
              ) : null}
            </Content>
          </Layout>
        </ProjectDetailsPageTemplate>
      );
    }

    case 'idle':
    case 'error':
    default: {
      return null;
    }
  }
};
